import clsx from 'clsx';
import React from 'react';
import { Button as ButtonComponent } from 'react-bootstrap';
import ReactHtmlParser from 'react-html-parser';

import { generateTrackingAttributes } from '../../../utils/tracking';
import styles from './styles.module.scss';

export interface ButtonProps {
  className?: string; // can pass extra custom class names to this component
  trackingProps?: { [key: string]: string }; // if you want click-tracking, add properties that match the EventProperties interface in "app/assets/javascripts/tracking.js"
  disabled?: boolean; // if true, button will be disabled
  href?: string; // if passed, the button will be a link
  onClick?: React.MouseEventHandler<HTMLButtonElement>; // onClick action for the button
  rounded?: 'slight' | 'full'; // determines how rounded the button is
  trackClick?: boolean; // if you want to track clicks, set this to true and add trackingProps
  type?: 'button' | 'submit' | 'reset'; // button type, defaults to "button"
  variant?: 'primary' | 'logo' | 'secondary' | 'light' | 'info' | 'muted' | 'link' | 'outline'; // determines color of button. If "link" button will look like a link and not a traditional button
  width?: 'full' | 'medium' | 'short'; // if short, button width will be 139px, if full, it will be 100%
  borderWidth?: 'thin' | 'medium'; // determines the thickness of the border
}

export interface ButtonPropsWithChildren extends ButtonProps {
  children: React.ReactNode;
}

export default function Button({
  children,
  className,
  borderWidth,
  disabled,
  href,
  onClick,
  rounded,
  variant,
  trackClick = false,
  trackingProps = {},
  type = 'button',
  width,
}: ButtonPropsWithChildren) {
  if (variant === 'link') {
    return (
      <ButtonComponent
        href={href}
        className={clsx(styles.LinkButton, className || '', {
          'track-click': trackClick,
        })}
        onClick={onClick}
        {...generateTrackingAttributes({
          page: window.location.href,
          type: 'link',
          ...trackingProps,
        })}
      >
        {children}
      </ButtonComponent>
    );
  }

  return (
    <ButtonComponent
      disabled={disabled}
      href={href}
      type={type}
      className={clsx(styles.Button, className || '', {
        [styles[`${variant}Variant`]]: variant,
        [styles[`${rounded}Rounded`]]: rounded,
        [styles[`${borderWidth}Border`]]: borderWidth,
        [styles[`${width}Width`]]: width,
        'track-click': trackClick,
      })}
      onClick={onClick}
      {...generateTrackingAttributes({
        ...trackingProps,
        page: window.location.href,
        type: 'link',
      })}
    >
      {ReactHtmlParser(children)}
    </ButtonComponent>
  );
}
